import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import { AiHelperService, ChatSession, ChatMessage } from './ai-helper.service';
import { SUPPORTED_LOCALES, LOCALE_GROUPS, LocaleConfig, LocaleGroup, localeLabel } from './ai-helper-locales';

@Component({
	selector: 'app-ai-helper',
	templateUrl: './ai-helper.component.html',
	styleUrls: ['./ai-helper.component.css']
})
export class AiHelperComponent implements OnInit, OnDestroy, AfterViewChecked {

	@ViewChild('messagesContainer') messagesContainer: ElementRef;
	@ViewChild('localePicker') localePickerRef: ElementRef;

	sessions: ChatSession[] = [];
	messages: ChatMessage[] = [];
	loading = false;
	activeSessionId: string | null = null;
	newMessage = '';

	// ── Locale picker state ───────────────────────────────────────
	showLocalePicker = false;
	activeLocale = 'en';
	readonly localeGroups = LOCALE_GROUPS;
	readonly allLocales = SUPPORTED_LOCALES;

	private subscriptions: Subscription[] = [];
	private shouldScroll = false;

	constructor(public aiHelper: AiHelperService) { }

	ngOnInit() {
		this.subscriptions.push(
			this.aiHelper.sessions$.subscribe(s => this.sessions = s),
			this.aiHelper.messages$.subscribe(m => {
				this.messages = m;
				this.shouldScroll = true;
			}),
			this.aiHelper.loading$.subscribe(l => this.loading = l),
			this.aiHelper.activeSessionId$.subscribe(id => this.activeSessionId = id),
			this.aiHelper.activeLocale$.subscribe(loc => this.activeLocale = loc),
		);
		this.aiHelper.loadSessions();
	}

	ngAfterViewChecked() {
		if (this.shouldScroll) {
			this.scrollToBottom();
			this.shouldScroll = false;
		}
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	// ── Session actions ───────────────────────────────────────────

	createSession() {
		this.aiHelper.createSession();
	}

	selectSession(session: ChatSession) {
		if (this.activeSessionId === session.id) return;
		this.aiHelper.loadMessages(session.id);
	}

	deleteSession(event: Event, session: ChatSession) {
		event.stopPropagation();
		this.aiHelper.deleteSession(session.id);
	}

	// ── Message actions ───────────────────────────────────────────

	sendMessage() {
		const text = this.newMessage.trim();
		if (!text || !this.activeSessionId || this.loading) return;
		this.newMessage = '';
		this.aiHelper.sendMessage(this.activeSessionId, text);
	}

	onKeyDown(event: KeyboardEvent) {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.sendMessage();
		}
	}

	// ── Locale picker ─────────────────────────────────────────────

	/** Return the group-filtered locale list for a given group label. */
	localesForGroup(group: LocaleGroup): LocaleConfig[] {
		return this.allLocales.filter(l => l.group === group);
	}

	/** Display label for the picker button (e.g., "EN", "FR-CA"). */
	get localeButtonLabel(): string {
		return localeLabel(this.activeLocale);
	}

	toggleLocalePicker(event: Event) {
		event.stopPropagation();
		this.showLocalePicker = !this.showLocalePicker;
	}

	selectLocale(locale: LocaleConfig, event: Event) {
		event.stopPropagation();
		if (!this.activeSessionId) return;
		this.aiHelper.setSessionLocale(this.activeSessionId, locale.code);
		this.showLocalePicker = false;
	}

	/** Close the picker when the user clicks anywhere outside of it. */
	@HostListener('document:click')
	onDocumentClick() {
		this.showLocalePicker = false;
	}

	// ── Message rendering ─────────────────────────────────────────

	getLines(content: string): string[] {
		return (content || '').split('\n');
	}

	isTableLine(line: string): boolean {
		return line.includes('|');
	}

	isBoldLine(line: string): boolean {
		return line.trimStart().startsWith('**');
	}

	private scrollToBottom() {
		try {
			if (this.messagesContainer) {
				this.messagesContainer.nativeElement.scrollTop = this.messagesContainer.nativeElement.scrollHeight;
			}
		} catch (err) { }
	}
}
